import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Gift, Globe, Loader2, Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { API, CONTACT } from "./site";

const COUNTRIES = ["South Africa", "United Kingdom", "United States", "Other"];
const GOALS = [
    "Reading and comprehension",
    "Grammar and punctuation",
    "Spelling and vocabulary",
    "Creative and structured writing",
    "Building English confidence",
    "Not sure yet",
];

const EMPTY = { parent_name: "", email: "", country: "", grade: "", goal: "", preferred_times: "", message: "" };

export const Contact = () => {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.country || !form.goal) {
            toast.error("Please choose your country and main learning goal.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            setSent(true);
            setForm(EMPTY);
        } catch (err) {
            toast.error("Something went wrong sending your enquiry. Please try again or reach us on WhatsApp.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" className="scroll-mt-24 border-y border-ground-line/60 bg-ground" data-testid="contact-section">
            <div className="mx-auto grid max-w-7xl gap-14 px-6 py-20 lg:grid-cols-12 lg:py-28">
                <motion.div
                    className="lg:col-span-5"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <p className="text-xs font-bold tracking-widest text-sun uppercase">Let's connect</p>
                    <h2 className="font-heading mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
                        Take the first step.
                    </h2>
                    <p className="mt-4 text-base text-mist sm:text-lg">
                        Interested in English support for your child? Send an enquiry and share a little about their
                        learning needs.
                    </p>
                    <ul className="mt-8 space-y-5">
                        <li className="flex items-start gap-4" data-testid="contact-serving">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sun/15 text-sun">
                                <Globe className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="font-heading font-bold text-ink">Serving</p>
                                <p className="text-sm text-mist">Families in South Africa, the United Kingdom and the United States</p>
                            </div>
                        </li>
                        <li className="flex items-start gap-4" data-testid="contact-offer">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sun/15 text-sun">
                                <Gift className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="font-heading font-bold text-ink">Introductory offer</p>
                                <p className="text-sm text-mist">Free introductory consultation</p>
                            </div>
                        </li>
                        <li className="flex items-start gap-4" data-testid="contact-email">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sun/15 text-sun">
                                <Mail className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="font-heading font-bold text-ink">Email</p>
                                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="text-sm text-sun underline-offset-4 transition-colors hover:underline">
                                    {CONTACT.email}
                                </a>
                            </div>
                        </li>
                        <li className="flex items-start gap-4" data-testid="contact-whatsapp">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sun/15 text-sun">
                                <MessageCircle className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="font-heading font-bold text-ink">Phone / WhatsApp</p>
                                <a href={CONTACT.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="text-sm text-sun underline-offset-4 transition-colors hover:underline">
                                    {CONTACT.phoneDisplay}
                                </a>
                            </div>
                        </li>
                    </ul>
                </motion.div>
                <motion.div
                    className="lg:col-span-7"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    {sent ? (
                        <div
                            data-testid="enquiry-success-message"
                            className="flex h-full flex-col items-center justify-center rounded-3xl border border-sun/50 bg-ground-deep/70 p-10 text-center shadow-2xl shadow-black/30"
                        >
                            <CheckCircle2 className="h-14 w-14 text-sun" />
                            <h3 className="font-heading mt-5 text-2xl font-extrabold text-ink">
                                Thank you — your enquiry has been sent!
                            </h3>
                            <p className="mt-3 max-w-md text-sm leading-relaxed text-mist">
                                YNova Education has received your enquiry and will be in touch soon to arrange your
                                free introductory consultation.
                            </p>
                            <Button
                                variant="outline"
                                onClick={() => setSent(false)}
                                data-testid="enquiry-send-another-btn"
                                className="mt-7 rounded-full border-sun/50 bg-transparent font-semibold text-sun hover:bg-sun/10 hover:text-sun"
                            >
                                Send another enquiry
                            </Button>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit}
                            data-testid="enquiry-form"
                            className="rounded-3xl border border-ground-line/70 bg-ground-deep/70 p-8 shadow-2xl shadow-black/30 sm:p-10"
                        >
                            <div className="grid gap-6 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="parent-name" className="text-mist">Parent or guardian name</Label>
                                    <Input
                                        id="parent-name"
                                        data-testid="enquiry-name-input"
                                        required
                                        value={form.parent_name}
                                        onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
                                        placeholder="Your name"
                                        className="border-ground-line bg-ground-soft/60 text-ink placeholder:text-mist/50 focus-visible:ring-sun"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-mist">Email address</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        data-testid="enquiry-email-input"
                                        required
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        placeholder="you@example.com"
                                        className="border-ground-line bg-ground-soft/60 text-ink placeholder:text-mist/50 focus-visible:ring-sun"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-mist">Country</Label>
                                    <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                                        <SelectTrigger data-testid="enquiry-country-select" className="border-ground-line bg-ground-soft/60 text-ink focus:ring-sun">
                                            <SelectValue placeholder="Choose your country" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {COUNTRIES.map((c) => (
                                                <SelectItem key={c} value={c} data-testid={`enquiry-country-option-${c.toLowerCase().replace(/ /g, "-")}`}>{c}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="grade" className="text-mist">Child's grade or school year</Label>
                                    <Input
                                        id="grade"
                                        data-testid="enquiry-grade-input"
                                        required
                                        value={form.grade}
                                        onChange={(e) => setForm({ ...form, grade: e.target.value })}
                                        placeholder="e.g. Grade 3, Year 4 or 3rd grade"
                                        className="border-ground-line bg-ground-soft/60 text-ink placeholder:text-mist/50 focus-visible:ring-sun"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-mist">Main English learning goal</Label>
                                    <Select value={form.goal} onValueChange={(v) => setForm({ ...form, goal: v })}>
                                        <SelectTrigger data-testid="enquiry-goal-select" className="border-ground-line bg-ground-soft/60 text-ink focus:ring-sun">
                                            <SelectValue placeholder="Choose a learning goal" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {GOALS.map((g) => (
                                                <SelectItem key={g} value={g}>{g}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="times" className="text-mist">Preferred days and times (with time zone)</Label>
                                    <Input
                                        id="times"
                                        data-testid="enquiry-times-input"
                                        value={form.preferred_times}
                                        onChange={(e) => setForm({ ...form, preferred_times: e.target.value })}
                                        placeholder="e.g. Weekdays 4–6pm (SAST / GMT / EST)"
                                        className="border-ground-line bg-ground-soft/60 text-ink placeholder:text-mist/50 focus-visible:ring-sun"
                                    />
                                </div>
                                <div className="space-y-2 sm:col-span-2">
                                    <Label htmlFor="message" className="text-mist">Message (optional)</Label>
                                    <Textarea
                                        id="message"
                                        data-testid="enquiry-message-input"
                                        rows={4}
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        placeholder="Tell us a little about your child's learning needs…"
                                        className="border-ground-line bg-ground-soft/60 text-ink placeholder:text-mist/50 focus-visible:ring-sun"
                                    />
                                </div>
                            </div>
                            <Button
                                type="submit"
                                size="lg"
                                disabled={sending}
                                data-testid="enquiry-submit-button"
                                className="mt-8 w-full rounded-full text-base font-bold shadow-xl shadow-sun/25 transition-transform duration-200 hover:scale-[1.02] sm:w-auto sm:px-10"
                            >
                                {sending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Send className="mr-2 h-5 w-5" />}
                                {sending ? "Sending…" : "Send Enquiry"}
                            </Button>
                            <p className="mt-4 text-xs text-mist/70">
                                Your enquiry goes straight to YNova Education. Prefer to chat? Message us on WhatsApp at {CONTACT.phoneDisplay}.
                            </p>
                        </form>
                    )}
                </motion.div>
            </div>
        </section>
    );
};
